import { isArray, keyBy } from "lodash";
import { createSelector, ParametricSelector, Selector } from "reselect";
import { User, Users } from "../../../normalizr/entities";
import { SortUtility } from "../../../utils/sort";
import { getGuestRoleId } from "../../AccountSettings/selectors";
import { AvailableUsersState } from "../types";

export const getAllUsers = (state: AvailableUsersState) => state.availableUsers?.users;
export const getUsersDataLoaded = (state: AvailableUsersState) => !!getAllUsers(state);

type SelectorSettings = {
    includeDisabledIfSelected?: boolean;
};

const defaultSelectorSettings: SelectorSettings = {
    includeDisabledIfSelected: true,
};

type Filter = (hash: Dictionary<number>, settings: SelectorSettings) => (u: User) => boolean;

const allFilter: Filter = (hash, settings) => x => x.IsEnabled || selectedFilter(hash, settings)(x);
const selectedFilter: Filter = (hash, settings) => x =>
    !!hash[x.UserId] && (settings.includeDisabledIfSelected || x.IsEnabled);

type Reducer<TResult = User[]> = {
    reducer: (previousValue: TResult, currentValue: User, currentIndex: number, array: User[]) => TResult;
    getInitialValue: () => TResult;
    emptyResult: TResult;
};

const emptyArray: User[] = [];

const arrayReducer: Reducer = {
    reducer: (r, u, i, array) => array,
    getInitialValue: () => [],
    emptyResult: emptyArray,
};
const entitiesReducer: Reducer<Users> = {
    reducer: (r, u) => ((r[u.UserId] = u), r),
    getInitialValue: () => ({}),
    emptyResult: null,
};

const buildUsers =
    <TResult>(
        filter: Filter,
        { reducer, getInitialValue, emptyResult }: Reducer<TResult>,
        settings: SelectorSettings
    ) =>
    (users: Users, selectedUsers: number[]): TResult => {
        if (!users) {
            return emptyResult;
        }

        settings = {
            ...defaultSelectorSettings,
            ...settings,
        };
        const usersHash = keyBy(selectedUsers || []);

        return Object.values(users).filter(filter(usersHash, settings)).reduce(reducer, getInitialValue());
    };

interface SelectorFactory<TResult> {
    <TState>(idsSelector?: F1<TState, number[]>, settings?: SelectorSettings): Selector<
        TState & AvailableUsersState,
        TResult
    >;
    // eslint-disable-next-line max-len
    <TState, TParams>(idsSelector?: F2<TState, TParams, number[]>, settings?: SelectorSettings): ParametricSelector<
        TState & AvailableUsersState,
        TParams,
        TResult
    >;
}

const selectorCreator =
    <TResult>(reducer: Reducer<TResult>, filter: Filter) =>
    (userIdsGetter: (...args: any[]) => number[], settings: SelectorSettings) =>
        createSelector(getAllUsers, userIdsGetter ?? (() => null), buildUsers(filter, reducer, settings));

/**
 * Might be used to display users in dropdowns and ect
 * By default includes all enabled users plus currently selected users (even if they are disabled)
 */
export const createAvailableUsersSelector: SelectorFactory<Users> = selectorCreator(entitiesReducer, allFilter);

/**
 * Might be used to display list of selected user and etc.
 * By default include disabled users.
 */
export const createUsersSelector: SelectorFactory<User[]> = selectorCreator(arrayReducer, selectedFilter);

/**
 * The same as createUsersSelector, but also sort users by name.
 */
export const createSortedUsersSelector: SelectorFactory<User[]> = (
    userIdsGetter: (...args: any[]) => number[],
    settings: SelectorSettings
) => withSortingByName(createUsersSelector(userIdsGetter, settings));

export const withoutGuests = <TSelector extends (...args: any[]) => Users>(usersSelector: TSelector) =>
    createSelector([usersSelector, getGuestRoleId], (users, guestRoleId) =>
        Object.values(users ?? {})
            .filter(x => x.RoleId !== guestRoleId)
            .reduce(entitiesReducer.reducer, entitiesReducer.getInitialValue())
    );

export const withSortingByName = <TSelector extends (...args: any[]) => Users | User[]>(usersSelector: TSelector) =>
    createSelector(usersSelector, users =>
        (isArray(users) ? users : Object.values(users)).sort(SortUtility.nameComparer)
    );

export const simpleIdsGetter = (state: AvailableUsersState, ids: number[]) => ids;
